"use client";
import {Button} from "@/components/ui/button";
import {RightSideCard} from "@/app/events/[id]/components/RightSideCard";
import {EventImageCarousel} from "@/app/events/[id]/components/EventImageCarousel";
import {useParams, useRouter, useSearchParams} from "next/navigation";
import {useEffect} from "react";
import {
    eventInSingleEventPage,
    gotoAnotherSingleEvent,
    loadEventById,
    updateAffiliateLink,
} from "@/app/events/store/single-page.store";
import {useSignals} from "@preact/signals-react/runtime";
import {signal} from "@preact/signals-react";
import {clearAllData, loadEventTickets,} from "@/app/events/[id]/store/buy-tickets.store";
import {formatedDateTime, formatedDateTimeOffsetUtc,} from "@/lib/date-time-utils";
import Link from "next/link";
import {CalendarClock, GlobeLock, Info, MapPinHouse, Share,} from "lucide-react";
import {EventStatus, IEventV2} from "@/app/dashboard/models/Event";
import GoogleMapCustom from "./[id]/components/GoogleMapCustom";
import {
    FacebookIcon,
    FacebookShareButton,
    LinkedinIcon,
    LinkedinShareButton,
    TwitterIcon,
    TwitterShareButton,
    WhatsappIcon,
    WhatsappShareButton,
} from "next-share";
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer";
import {FaInstagram} from "react-icons/fa";

interface SharedSingleEventProps {
  eventId?: string;
  previewToken?: string | null;
  isAffiliate?: boolean;
  ref?: string | null;
}

export const SharedSingleEvent: React.FC<SharedSingleEventProps> = ({ 
  eventId,
  previewToken = null,
  isAffiliate = false,
  ref = null
}) => {
    useSignals();
    const {id} = useParams();
    const searchParams = useSearchParams();
    const router = useRouter();
    
    // Use provided props or fall back to URL params
    const actualEventId = eventId || (id as string);
    const actualPreviewToken = previewToken || searchParams?.get("t");
    const actualIsAffiliate = isAffiliate || (searchParams?.get("isAffiliate") === String(true));
    const actualRef = ref || searchParams?.get("ref");
    
    const isDrawerOpen = signal(false);
    const navigate = router.push || ((url: string) => {
        if (typeof window !== 'undefined') {
            window.location.href = url;
        }
    });


    const currentHost = process.env.NEXT_PUBLIC_APP_URL || window?.location?.origin;

    useEffect(() => {
        updateAffiliateLink({ref: actualRef, isAffiliate: actualIsAffiliate})
    }, [actualRef, actualIsAffiliate]);

    useEffect(() => {
        clearAllData();
        loadEventById(actualEventId, actualPreviewToken, navigate);
        loadEventTickets(actualEventId);
        const handleResize = () => {
            isDrawerOpen.value = false;
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [actualEventId, actualPreviewToken]);

    const event = eventInSingleEventPage.value;
    const location = event?.location;

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-5">
            <div className="flex flex-col lg:block">
                <div className="float-left lg:w-2/3 lg:pr-10">
                    <div className="relative">
                        <EventImageCarousel
                            mainImage={event?.image || ""}
                            eventImages={event?.eventImages}
                            alt={event?.name || "Event"}
                        />
                    </div>
                    
                    {/* Mobile Share Button */}
                    <div className="lg:hidden mb-4">
                        <Drawer open={isDrawerOpen.value} onOpenChange={(open) => isDrawerOpen.value = open}>
                            <DrawerTrigger asChild>
                                <Button
                                    size="icon"
                                    className="rounded-full border-none bg-stone-neutral-0/40 focus:bg-stone-neutral-0/40 hover:bg-stone-neutral-0/40 backdrop-blur-md"
                                >
                                    <Share size={20}/>
                                </Button>
                            </DrawerTrigger>
                            <DrawerContent>
                                <DrawerHeader>
                                    <DrawerTitle
                                        className="text-base text-center font-normal text-stone-neutral-10">
                                        Share this event
                                    </DrawerTitle>
                                </DrawerHeader>
                                <div className="flex items-center justify-center mb-4 gap-6">
                                    <FacebookShareButton
                                        url={`${currentHost}/events/${actualEventId}`}
                                        quote={event?.name}
                                        hashtag="#CoolEvent"
                                    >
                                        <FacebookIcon size={32} round/>
                                    </FacebookShareButton>
                                    <TwitterShareButton
                                        url={`${currentHost}/events/${actualEventId}`}
                                        title={event?.name}
                                    >
                                        <TwitterIcon size={32} round/>
                                    </TwitterShareButton>
                                    <LinkedinShareButton
                                        url={`${currentHost}/events/${actualEventId}`}
                                    >
                                        <LinkedinIcon size={32} round/>
                                    </LinkedinShareButton>
                                    <WhatsappShareButton
                                        url={`${currentHost}/events/${actualEventId}`}
                                        title={event?.name}
                                        separator=":: "
                                    >
                                        <WhatsappIcon size={32} round/>
                                    </WhatsappShareButton>
                                    <a
                                        aria-label="Share to instagram"
                                        href={`https://www.instagram.com/?url=${encodeURIComponent(
                                            `${currentHost}/events/${actualEventId}`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaInstagram size={32}/>
                                    </a>
                                </div>
                                <DrawerFooter>
                                    <DrawerClose>
                                        <a>Cancel</a>
                                    </DrawerClose>
                                </DrawerFooter>
                            </DrawerContent>
                        </Drawer>
                    </div>
                    <div>
                        <div className="flex items-center gap-4 mb-5">
                            <h1 className="text-2xl md:text-4xl font-extraBold text-ocean-blue-9">
                                {event?.name}
                            </h1>
                            {event?.status === EventStatus.PublishedAsPrivate && (
                                <div
                                    className="flex items-center gap-2 bg-ocean-blue-3 text-ocean-blue-9 text-base p-3 rounded-lg">
                                    <GlobeLock size={20}/>
                                    <div>This is a private event</div>
                                </div>
                            )}
                        </div>
                        <div
                            className="text-md text-stone-neutral-10 mb-6 [&_*]:break-words"
                            dangerouslySetInnerHTML={{
                                __html: event?.htmlDescription || <span className="text-stone-neutral-11">No description added to this event</span>,
                            }}>
                        </div>
                        <div className="mb-10">
                            <h2 className="text-xl md:text-2xl font-bold mb-2 text-ocean-blue-9">
                                Date and time
                            </h2>
                            <div className="flex items-center gap-2">
                                <CalendarClock size={20} className="stroke-ocean-blue-9"/>
                                <p className="text-base text-stone-neutral-12">
                                    {event?.startTimeStamp &&
                                        location?.timeZone &&
                                        formatedDateTimeOffsetUtc(
                                            event.startTimeStamp,
                                            location.timeZone,
                                            "dddd, MMMM D YYYY [at] h:mm A"
                                        )}
                                </p>
                            </div>
                        </div>
                        <div className="mb-5">
                            <h2 className="text-xl md:text-2xl font-bold mb-2 text-ocean-blue-9">
                                Venue
                            </h2>
                            <div className="flex items-start gap-2">
                                <MapPinHouse size={20} className="stroke-ocean-blue-9"/>
                                <div>
                                    <p className="text-base font-bold text-stone-neutral-12">
                                        {location?.name}
                                    </p>
                                    <p className="text-base text-stone-neutral-11">
                                        {location?.address}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {(location?.lat != null && location?.lon != null && (location.lat !== 0 || location.lon !== 0)) ? (
                            <div className="mapouter mb-10 rounded-lg overflow-hidden">
                                <GoogleMapCustom/>
                            </div>
                        ) : null}
                        <div className="mb-10">
                            <h2 className="text-xl md:text-2xl font-bold mb-2 text-ocean-blue-9">
                                Other info
                            </h2>
                            <div className="flex items-center gap-2">
                                <Info size={20} className="stroke-ocean-blue-9"/>
                                <p className="text-base text-stone-neutral-12">
                                    No refunds will be issued
                                </p>
                            </div>
                        </div>
                        <h2 className="text-xl md:text-2xl font-bold mb-2 text-ocean-blue-9">
                            Organized by
                        </h2>
                        <div
                            className="mb-10 bg-stone-neutral-0 border border-stone-neutral-4 rounded-lg p-4 flex flex-col sm:flex-row gap-4 justify-between sm:items-center">
                            <div className="flex items-center grow gap-4">
                                <img
                                    src={event?.organization?.organizationLogo}
                                    alt={event?.organization?.organizationName}
                                    className="w-16 h-16 rounded-full"
                                />
                                <div>
                                    <h3 className="text-xl font-bold text-ocean-blue-10 p-0">
                                        {event?.organization?.organizationName}
                                    </h3>
                                    <div className="flex items-center gap-1">
                                        <p className="text-base font-bold text-stone-neutral-12">
                                            {event?.hostedEventCount}
                                        </p>
                                        <p className="text-base text-stone-neutral-11">
                                            Events hosted
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <Link href={`/organization/${event?.organization?.id}`}>
                                <Button
                                    className="text-base text-ocean-blue-10 p-0"
                                    variant="link"
                                    size="sm"
                                >
                                    View profile
                                </Button>
                            </Link>
                        </div>
                        {event?.upcomingEvents && (
                            <div className="mb-10">
                                <h2 className="text-xl font-bold mb-2 text-ocean-blue-9">
                                    More events from this organizer
                                </h2>
                                <div className="flex flex-wrap justify-start w-full">
                                    {event?.upcomingEvents?.map(
                                        (upcomingEvent: IEventV2, index) => (
                                            <div
                                                key={index}
                                                className="group p-1 rounded-xl hover:bg-stone-neutral-3 cursor-pointer w-full"
                                                onClick={() =>
                                                    gotoAnotherSingleEvent(navigate, upcomingEvent.id)
                                                }
                                            >
                                                <div className="flex gap-3 items-center">
                                                    <div className="overflow-hidden rounded-lg w-36 h-36">
                                                        <img
                                                            src={upcomingEvent?.image}
                                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                                            alt="Event image"
                                                        />
                                                    </div>
                                                    <div className="ml-1 mb-1 flex-1">
                                                        <p className="text-md font-bold whitespace-break-spaces text-stone-neutral-10">
                                                            {upcomingEvent?.name}
                                                        </p>
                                                        <p className="text-base whitespace-break-spaces text-stone-neutral-11">
                                                            {formatedDateTime(upcomingEvent?.startTimeStamp)} ---
                                                        </p>
                                                        <p className="text-base whitespace-break-spaces text-stone-neutral-11">
                                                            {upcomingEvent.location.name}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className="float-right z-50 w-full lg:w-1/3 fixed lg:sticky bottom-0 left-0 lg:bottom-auto lg:top-[5.375rem]">
                    <RightSideCard/>
                    {/* Share card  */}
                    <div className="hidden lg:block mt-4 p-4 bg-white border rounded-lg shadow-sm">
                        <h3 className="text-lg font-bold mb-2">Share this event</h3>
                        <div className="flex items-center gap-2">
                            <FacebookShareButton
                                url={`${currentHost}/events/${actualEventId}`}
                                quote={event?.name}
                                hashtag="#CoolEvent"
                            >
                                <FacebookIcon size={32} round/>
                            </FacebookShareButton>
                            <TwitterShareButton
                                url={`${currentHost}/events/${actualEventId}`}
                                title={event?.name}
                            >
                                <TwitterIcon size={32} round/>
                            </TwitterShareButton>
                            <LinkedinShareButton
                                url={`${currentHost}/events/${actualEventId}`}
                            >
                                <LinkedinIcon size={32} round/>
                            </LinkedinShareButton>
                            <WhatsappShareButton
                                url={`${currentHost}/events/${actualEventId}`}
                                title={event?.name}
                                separator=":: "
                            >
                                <WhatsappIcon size={32} round/>
                            </WhatsappShareButton>
                        </div>
                    </div>
                </div>
                <br className="clear-both"/>
            </div>
        </div>
    );
};

